import { useEffect, useState } from 'react'
import { AlertTriangle, Check, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import { mintConfirmToken } from '../api/queries'
import type { DestructiveConfirmMessage, ExecuteConfirmedResult } from '../hooks/useCarlaChat'

export interface DestructiveConfirmCardProps {
  msg: DestructiveConfirmMessage
  /** Mints a token, then dispatches the confirmed action via the
   *  deterministic /api/agent/execute-confirmed endpoint (no Claude
   *  re-emit). `dispatched: true` only if the backend actually ran the
   *  tool — `false` means nothing ran (token rejected, infra error,
   *  network failure) and the card should stay retryable, with `error`
   *  carrying the specific reason to show instead of a generic message.
   *  The chat hook owns the wiring; this card just calls the callback
   *  and reflects the outcome it reports. */
  onConfirm: (token: string, msg: DestructiveConfirmMessage) => Promise<ExecuteConfirmedResult>
  onUpdateStatus: (status: DestructiveConfirmMessage['status']) => void
  /** True while CARLA is still streaming her reply. The confirmed action
   *  can't dispatch mid-stream, so a Confirm click is queued and runs as
   *  soon as the stream ends (2026-10-06: clicks during the stream were
   *  silently refused with a generic "not executed" error). */
  busy?: boolean
}

/**
 * Sprint 29 — inline destructive-action confirmation card.
 *
 * Rendered when CARLA's tool_result event carries
 * `confirm_token_required: true` (Sprint 25's server-side T3 gate
 * rejected the dispatch). Shows the tool name + parameters + a
 * Confirm / Cancel pair. On Confirm: mints a single-use token via
 * POST /api/agent/confirm, then asks the chat hook to dispatch the
 * confirmed action directly against POST /api/agent/execute-confirmed
 * (2026-05-27) — the backend runs the exact (tool, params) pair it
 * hashed at issuance, with no Claude re-emit involved. The card only
 * flips to "Confirmed" once that dispatch is reported as having
 * actually happened; otherwise it surfaces a retryable error.
 *
 * Distinct from ConfirmationCard.tsx — that's the older Sprint 06
 * staged-action pattern (action_id + /api/actions/{id}/confirm). This
 * is the new confirm_tokens pattern that protects every T3 tool
 * (force_picking_done, call_odoo_method, …) automatically.
 */
export function DestructiveConfirmCard({
  msg,
  onConfirm,
  onUpdateStatus,
  busy = false,
}: DestructiveConfirmCardProps) {
  const [loading, setLoading] = useState(false)
  const [queued, setQueued] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isPending = msg.status === 'pending'
  const isConfirmed = msg.status === 'confirmed'
  const isRejected = msg.status === 'rejected' || msg.status === 'expired'

  async function handleConfirm() {
    setError(null)
    setLoading(true)
    try {
      const issued = await mintConfirmToken(msg.tool_name, msg.tool_params)
      const { dispatched, error: reason } = await onConfirm(issued.token, msg)
      if (dispatched) {
        onUpdateStatus('confirmed')
      } else {
        setError(reason ?? 'The action was not executed — nothing changed. You can try confirming again.')
        // Stay 'pending' so the Confirm/Cancel buttons remain visible.
      }
    } catch (e) {
      setError((e as Error).message || 'Could not issue confirmation token.')
      // Stay 'pending' so the user can retry — token mint failure is
      // typically transient (network, 500). Token validation failures
      // surface separately as a follow-up tool denial.
    } finally {
      setLoading(false)
    }
  }

  function handleConfirmClick() {
    if (busy) {
      setError(null)
      setQueued(true)
      return
    }
    void handleConfirm()
  }

  // Run a queued confirm once CARLA's reply finishes. The token is minted
  // here, not at click time, so its 60s TTL starts when it is used.
  useEffect(() => {
    if (queued && !busy && isPending) {
      // One-shot reset of the queue flag before firing — not a render cascade.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQueued(false)
      void handleConfirm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queued, busy, isPending])

  function handleReject() {
    setQueued(false)
    onUpdateStatus('rejected')
  }

  return (
    <div
      className={cn(
        'rounded-sm border bg-surface',
        isPending && 'border-warning-border',
        isConfirmed && 'border-success-border bg-success-bg',
        isRejected && 'border-border-subtle opacity-70',
      )}
    >
      <header className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
        <AlertTriangle size={16} className="flex-shrink-0 text-warning-text" />
        <span className="flex-1 text-sm font-semibold text-primary">
          Confirm destructive action
        </span>
        {isConfirmed ? (
          <span className="rounded-xs bg-success px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-on-accent">
            Confirmed
          </span>
        ) : null}
        {isRejected ? (
          <span className="rounded-xs bg-muted px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-secondary">
            Cancelled
          </span>
        ) : null}
      </header>
      <div className="space-y-2 px-3 py-2 text-sm text-primary">
        <p className="m-0 leading-snug">{msg.reason}</p>
        <div className="rounded-xs bg-surface-sunk px-2 py-1.5">
          <div className="text-2xs font-semibold uppercase tracking-wide text-tertiary">
            Tool
          </div>
          <div className="text-sm font-semibold text-primary">{msg.tool_name}</div>
          {Object.keys(msg.tool_params).length > 0 ? (
            <>
              <div className="mt-1.5 text-2xs font-semibold uppercase tracking-wide text-tertiary">
                Parameters
              </div>
              <pre className="m-0 whitespace-pre-wrap break-words text-xs text-secondary">
                {JSON.stringify(msg.tool_params, null, 2)}
              </pre>
            </>
          ) : null}
        </div>
      </div>
      {error ? (
        <div className="border-t border-border-subtle bg-danger-bg px-3 py-2 text-xs text-danger-text">
          {error}
        </div>
      ) : null}
      {isPending ? (
        <footer className="flex items-center gap-2 border-t border-border-subtle px-3 py-2">
          {loading ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-accent-text">
              <Loader2 size={14} className="animate-spin" /> Running…
            </span>
          ) : queued ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-sm text-accent-text">
                <Loader2 size={14} className="animate-spin" /> Confirmed — runs as soon as CARLA finishes replying
              </span>
              <Button variant="ghost" size="sm" onClick={handleReject}>
                <X size={14} />
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" size="sm" onClick={handleConfirmClick}>
                <Check size={14} />
                Confirm
              </Button>
              <Button variant="ghost" size="sm" onClick={handleReject}>
                <X size={14} />
                Cancel
              </Button>
            </>
          )}
        </footer>
      ) : null}
    </div>
  )
}
